import ApiService from "@/core/services/api.service";

/**
 * Front-end seam for the governed INSIGHT posting endpoints.
 *
 * Mirrors workbench-service / billing-review-service: ApiService (base `/services`),
 * Azure AD bearer via interceptor, SUCCESS envelope `{ status, result }` → `data.result`.
 */

/**
 * Read-only assemble + pre-check diagnostic for a billing line.
 * @param {string} ledgerRowId
 * @returns {Promise<Object>}
 */
export function assembleInspect(ledgerRowId) {
  return ApiService.get(
    `/workbench/billing/assemble-inspect/${encodeURIComponent(ledgerRowId)}`
  ).then(({ data }) => data.result);
}

/**
 * Create the governed INSIGHT draft for a billing line.
 * @param {string} ledgerRowId
 * @param {Object} [body] optional — e.g. { recoveryAcknowledgement }
 * @returns {Promise<Object>}
 */
export function createDraft(ledgerRowId, body) {
  return ApiService.post(
    `/workbench/actions/create-draft/${encodeURIComponent(ledgerRowId)}`,
    body && typeof body === "object" ? body : {}
  ).then(({ data }) => data.result);
}

/**
 * Read-only field-by-field comparison of the existing INSIGHT renewal against the reviewed draft.
 * @param {string} ledgerRowId
 * @param {string} [recoveryAcknowledgement]
 * @returns {Promise<Object>}
 */
export function readbackInspect(ledgerRowId, recoveryAcknowledgement) {
  const query = recoveryAcknowledgement
    ? `?recoveryAcknowledgement=${encodeURIComponent(recoveryAcknowledgement)}`
    : "";
  return ApiService.get(
    `/workbench/billing/readback-inspect/${encodeURIComponent(ledgerRowId)}${query}`
  ).then(({ data }) => data.result);
}

/**
 * Read-only forensic for a billing line that has already been dispatched: what AR allocation and
 * invoice debtor is INSIGHT holding on the posted transaction now, versus what the draft committed.
 * One GetEx read; writes nothing.
 * @param {string} ledgerRowId
 * @returns {Promise<Object>}
 */
export function postedAllocation(ledgerRowId) {
  return ApiService.get(
    `/workbench/billing/posted-allocation/${encodeURIComponent(ledgerRowId)}`
  ).then(({ data }) => data.result);
}

/**
 * Read-only diagnostics for the invoice debtor. INSIGHT derives the debtor when the transaction is
 * posted, from the AR allocation; every call here is a read or a Save:false step and writes nothing.
 * @param {string} ledgerRowId
 * @param {string} [recoveryAcknowledgement]
 * @returns {Promise<Object>}
 */
export function debtorProbe(ledgerRowId, recoveryAcknowledgement) {
  const query = recoveryAcknowledgement
    ? `?recoveryAcknowledgement=${encodeURIComponent(recoveryAcknowledgement)}`
    : "";
  return ApiService.get(
    `/workbench/billing/debtor-probe/${encodeURIComponent(ledgerRowId)}${query}`
  ).then(({ data }) => data.result);
}

/**
 * Generate a schedule via Desk — identifiers only. Never sends money, vehicle or term figures;
 * Desk assembles those from source (the bound policy_term_snapshot etc.) or refuses by name. See
 * docs/insight-posting/20260728-schedule-generation-design.md in the underwriting-workbench repo.
 * @param {string} ledgerRowId
 * @param {{policyNumber?: string, expectedSnapshotHash?: string}} [identifiers]
 * @returns {Promise<Object>}
 */
export function generateSchedule(ledgerRowId, identifiers) {
  return ApiService.post(
    `/workbench/actions/generate-schedule/${encodeURIComponent(ledgerRowId)}`,
    identifiers && typeof identifiers === "object" ? identifiers : {}
  ).then(({ data }) => data.result);
}

/** Generate a sticky documents-only schedule without creating or posting a transaction. */
export function generateScheduleDocument(ledgerRowId, body) {
  return ApiService.post(
    `/workbench/actions/generate-schedule-document/${encodeURIComponent(ledgerRowId)}`,
    body && typeof body === "object" ? body : {}
  ).then(({ data }) => data.result);
}

/** Read the durable documents-only schedule snapshot for a billing line. */
export function scheduleDocumentStatus(ledgerRowId) {
  return ApiService.get(
    `/workbench/schedule-document-status/${encodeURIComponent(ledgerRowId)}`
  ).then(({ data }) => data.result);
}

/** Attach a generated schedule to an existing INSIGHT transaction without posting it. */
export function attachSchedulePosted(ledgerRowId, body) {
  return ApiService.post(
    `/workbench/actions/attach-schedule-posted/${encodeURIComponent(ledgerRowId)}`,
    body && typeof body === "object" ? body : {}
  ).then(({ data }) => data.result);
}

/**
 * Download a generated schedule DOCX by renderToken (bytes from the server-side generate cache).
 * @param {string} ledgerRowId
 * @param {string} renderToken
 * @returns {Promise<Blob>}
 */
export function downloadGeneratedSchedule(ledgerRowId, renderToken) {
  return ApiService.get(
    `/workbench/actions/schedule-download/${encodeURIComponent(ledgerRowId)}`,
    {
      params: { renderToken: renderToken },
      responseType: "blob"
    }
  ).then(({ data }) => data);
}

/**
 * Attach a schedule document to the draft for a billing line. Accepts either:
 *  - a base64 string — the original file-upload path, unchanged; or
 *  - a payload object { renderToken, contentBase64, sha256 } from generateSchedule's response, so a
 *    generated render can be attached by token without resending the whole file. contentBase64/sha256
 *    travel alongside the token as a best-effort fallback if the server-side render cache has expired.
 * @param {string} ledgerRowId
 * @param {string|{renderToken?: string, contentBase64?: string, sha256?: string}} payload
 * @returns {Promise<Object>}
 */
export function attachSchedule(ledgerRowId, payload) {
  const body =
    typeof payload === "string"
      ? { contentBase64: payload }
      : {
          renderToken: (payload && payload.renderToken) || undefined,
          contentBase64: (payload && payload.contentBase64) || undefined,
          sha256: (payload && payload.sha256) || undefined
        };
  return ApiService.post(
    `/workbench/actions/attach-schedule/${encodeURIComponent(ledgerRowId)}`,
    body
  ).then(({ data }) => data.result);
}

/**
 * Post the drafted transaction. Body fields are the operator confirm step:
 * accountingMonth, humanConfirmation, policyNumberTyped, idempotencyKey,
 * expectedMoney, expectedDebtorId (optional documentGenerationOptions).
 * @param {string} ledgerRowId
 * @param {Object} body
 * @returns {Promise<Object>}
 */
export function post(ledgerRowId, body) {
  return ApiService.post(
    `/workbench/actions/post/${encodeURIComponent(ledgerRowId)}`,
    body || {}
  ).then(({ data }) => data.result);
}

/**
 * Read-only posting status for a billing line.
 * @param {string} ledgerRowId
 * @returns {Promise<Object>}
 */
export function postingStatus(ledgerRowId) {
  return ApiService.get(
    `/workbench/posting-status/${encodeURIComponent(ledgerRowId)}`
  ).then(({ data }) => data.result);
}

/**
 * Retry Neon / Zoho reconciliation after a confirmed post hold. Never posts again.
 * @param {string} ledgerRowId
 * @returns {Promise<Object>}
 */
export function continueReconcile(ledgerRowId) {
  return ApiService.post(
    `/workbench/actions/continue-reconcile/${encodeURIComponent(ledgerRowId)}`,
    {}
  ).then(({ data }) => data.result);
}

export default {
  assembleInspect,
  createDraft,
  readbackInspect,
  postedAllocation,
  debtorProbe,
  generateSchedule,
  generateScheduleDocument,
  scheduleDocumentStatus,
  attachSchedulePosted,
  downloadGeneratedSchedule,
  attachSchedule,
  post,
  postingStatus,
  continueReconcile
};
